(() => {
  'use strict';
  if (window.AWGarageExactContextP2?.version) return;

  const VERSION = '1.0.0';
  const POLICY = 'persisted-handle-must-resolve-in-aw-model-graph-and-match-declared-fields;unique-legacy-fallback;never-creates-compatibility';
  const KEYS = {
    replicas: 'aw_garage_replicas_v1',
    active: 'aw_garage_active_replica_v1',
    exactCache: 'aw_personal_exact_model_v1'
  };
  const URL = '/pages/compatibilite-airsoft';
  const norm = (value = '') => String(value || '').replace(/[₂²]/g, '2').replace(/[₀⁰]/g, '0').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/&/g, ' et ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const parse = (storage, key, fallback) => {
    try { return JSON.parse(storage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };
  const replicas = () => {
    const value = parse(localStorage, KEYS.replicas, []);
    return Array.isArray(value) ? value : [];
  };
  const activeId = () => String(parse(localStorage, KEYS.active, '') || '').trim();
  const activeReplica = () => {
    const id = activeId();
    return id ? replicas().find((item) => String(item?.id || '') === id) || null : null;
  };
  const modelNodes = () => Array.from(document.querySelectorAll('[data-aw-tool-model],[data-aw-model-context-model]'));
  const record = (node) => node ? {
    handle: String(node.dataset.handle || '').trim(),
    label: String(node.dataset.label || '').trim(),
    model: String(node.dataset.model || '').trim(),
    manufacturer: String(node.dataset.manufacturer || '').trim(),
    platform: String(node.dataset.platform || '').trim(),
    platformLabel: String(node.dataset.platformLabel || '').trim(),
    type: String(node.dataset.type || '').trim()
  } : null;

  let baseResolve = null;
  let baseCompatibilityUrl = null;
  const captureBridge = () => {
    const bridge = window.AWEcosystemP1Bridge;
    if (!bridge) return null;
    if (!baseResolve && typeof bridge.resolveExactModel === 'function') baseResolve = bridge.resolveExactModel.bind(bridge);
    if (!baseCompatibilityUrl && typeof bridge.compatibilityUrl === 'function') baseCompatibilityUrl = bridge.compatibilityUrl.bind(bridge);
    return bridge;
  };
  const valuesMatch = (declared, canonical) => !norm(declared) || !norm(canonical) || norm(declared) === norm(canonical);
  const isConsistent = (replica, candidate) => {
    if (!replica || !candidate?.handle) return false;
    if (!valuesMatch(replica.manufacturer, candidate.manufacturer)) return false;
    if (!valuesMatch(replica.model, candidate.model)) return false;
    if (!valuesMatch(replica.type, candidate.type)) return false;
    const declaredPlatform = norm(replica.platform);
    const canonicalPlatforms = new Set([candidate.platform, candidate.platformLabel].map(norm).filter(Boolean));
    if (declaredPlatform && canonicalPlatforms.size && !canonicalPlatforms.has(declaredPlatform)) return false;
    return true;
  };
  const byHandle = (handle) => {
    const wanted = norm(handle);
    if (!wanted) return null;
    const matches = modelNodes().map(record).filter((item) => item?.handle && norm(item.handle) === wanted);
    const unique = new Map(matches.map((item) => [norm(item.handle), item]));
    return unique.size === 1 ? [...unique.values()][0] : null;
  };
  const resolveExactModel = (replica) => {
    if (!replica) return null;
    const persisted = byHandle(replica.modelHandle || '');
    if (persisted && isConsistent(replica, persisted)) return persisted;
    captureBridge();
    const legacy = baseResolve ? baseResolve(replica) : null;
    return legacy && isConsistent(replica, legacy) ? legacy : null;
  };
  const signature = (replica) => [replica?.id, replica?.manufacturer, replica?.model, replica?.platform, replica?.type].map(norm).join('|');
  const cacheExactModel = (replica, candidate) => {
    if (!replica || !candidate?.handle) return false;
    try {
      sessionStorage.setItem(KEYS.exactCache, JSON.stringify({
        signature: signature(replica),
        handle: norm(candidate.handle),
        source: 'garage_exact_model_graph_validated',
        resolvedAt: new Date().toISOString()
      }));
      return true;
    } catch (_) { return false; }
  };
  const compatibilityUrl = (replica = activeReplica()) => {
    const candidate = resolveExactModel(replica);
    if (!candidate?.handle) {
      captureBridge();
      return baseCompatibilityUrl ? baseCompatibilityUrl(replica) : URL;
    }
    const url = new URL(URL, location.origin);
    url.searchParams.set('aw_model', candidate.handle);
    url.searchParams.set('aw_from', 'garage');
    return `${url.pathname}${url.search}`;
  };
  const canonicalDetail = (replica, candidate) => ({
    platform: candidate.platform || replica.platform || '',
    platformLabel: candidate.platformLabel || candidate.platform || replica.platform || '',
    manufacturer: candidate.manufacturer || replica.manufacturer || '',
    manufacturerLabel: candidate.manufacturer || replica.manufacturer || '',
    type: candidate.type || replica.type || '',
    typeLabel: candidate.type || replica.type || '',
    model: candidate.model || replica.model || '',
    modelLabel: candidate.label || candidate.model || replica.model || '',
    modelHandle: candidate.handle
  });
  const writeCanonicalContext = (replica, candidate, reason = 'refresh') => {
    if (!replica || !candidate?.handle) return null;
    cacheExactModel(replica, candidate);
    const detail = canonicalDetail(replica, candidate);
    let context = null;
    try { context = window.AWAirsoftContext?.write?.(detail, 'garage-exact-model-p2') || null; }
    catch (_) { context = null; }
    try { window.AWPersonalContext?.apply?.(`garage_exact:${reason}`); } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('aw:garage-exact-context-resolved', {
        detail: { replicaId: String(replica.id || ''), modelHandle: candidate.handle, reason, context }
      }));
    } catch (_) {}
    return context;
  };
  const updateLinks = () => {
    const allReplicas = replicas();
    document.querySelectorAll('[data-aw-go-compatibility]').forEach((link) => {
      const id = String(link.dataset.awGoCompatibility || '').trim();
      const replica = allReplicas.find((item) => String(item?.id || '') === id) || null;
      const candidate = resolveExactModel(replica);
      if (!candidate?.handle) return;
      link.href = compatibilityUrl(replica);
      link.dataset.awGarageExactModel = candidate.handle;
    });
    const active = activeReplica();
    const candidate = resolveExactModel(active);
    if (!candidate?.handle) return candidate;
    document.querySelectorAll('[data-aw-ecosystem-context] a[href*="/pages/compatibilite-airsoft"]').forEach((link) => {
      link.href = compatibilityUrl(active);
      link.dataset.awGarageExactModel = candidate.handle;
    });
    return candidate;
  };
  const patchPublicBridge = () => {
    const bridge = captureBridge();
    if (!bridge || bridge.__awGarageExactP2Patched) return Boolean(bridge);
    bridge.resolveExactModel = resolveExactModel;
    bridge.compatibilityUrl = compatibilityUrl;
    bridge.__awGarageExactP2Patched = true;
    return true;
  };
  let timer = 0;
  const apply = (reason = 'refresh') => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      patchPublicBridge();
      const replica = activeReplica();
      const candidate = updateLinks() || resolveExactModel(replica);
      if (replica && candidate?.handle) writeCanonicalContext(replica, candidate, reason);
    }, 0);
  };

  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('[data-aw-go-compatibility]');
    if (!link) return;
    const replica = replicas().find((item) => String(item?.id || '') === String(link.dataset.awGoCompatibility || '')) || null;
    const candidate = resolveExactModel(replica);
    if (!candidate?.handle) return;
    link.href = compatibilityUrl(replica);
    queueMicrotask(() => writeCanonicalContext(replica, candidate, 'compatibility_click'));
  }, true);
  ['aw:garage-replica-selected','aw:garage-context-changed','aw:p2-garage-updated','aw:personal-context-changed'].forEach((name) => {
    window.addEventListener(name, () => apply(name));
  });
  window.addEventListener('storage', (event) => {
    if ([KEYS.replicas, KEYS.active].includes(event.key)) apply(`storage:${event.key}`);
  });
  document.addEventListener('shopify:section:load', () => apply('shopify:section:load'));

  window.AWGarageExactContextP2 = {
    version: VERSION,
    policy: POLICY,
    resolveExactModel,
    compatibilityUrl,
    writeCanonicalContext,
    updateLinks,
    apply
  };
  const start = () => {
    [0, 100, 500, 1500].forEach((delay) => window.setTimeout(() => apply(`start:${delay}`), delay));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();